import SharePage from "./pages/SharePage";
import { useAuth } from "./context/AuthContext";
// FILE: frontend/src/App.tsx
import * as React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import RequireAuth from "./components/RequireAuth";
import AdminRoute from "./components/AdminRoute";
import Landing from "./pages/Landing";
import Home from "./pages/Home";
import VideoPlayer from "./pages/VideoPlayer";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/LoginPage";
import HistoryPage from "./pages/HistoryPage";
import LayoutEditor from "./pages/LayoutEditor";

// Alias-based imports (ensure files exist and casing matches)
import Backgrounds from "@/pages/Backgrounds";
import AnimatedBg from "@/pages/AnimatedBg";
import AnimatedQuiz from "@/pages/AnimatedQuiz";
import Disclaimer from "./pages/Disclaimer";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import FAQ from "@/pages/FAQ";
import RefundPolicy from "@/pages/RefundPolicy";
import AccountPage from "@/pages/AccountPage";
import CreditsPage from "@/pages/Credits";
// Lazy-loaded pages to improve initial load
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const RenderEditor = React.lazy(() => import("./pages/RenderEditor"));
const FlashcardsPage = React.lazy(() => import("./pages/Flashcards"));
const PSEOFlashcardsLanding = React.lazy(() => import("./pages/PSEOFlashcardsLanding"));

function RenderRedirect(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  return <Navigate to={`/renders/${id}/edit`} replace />;
}

function DismissibleBanner({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}): JSX.Element | null {
  if (!show) return null;

  return (
    <div className="bg-green-100 dark:bg-green-900/40 text-center p-4 rounded-lg shadow-md flex justify-between items-center border border-green-200 dark:border-green-900/60">
      <div className="flex items-center gap-3">
        <span className="text-2xl" role="img" aria-label="Party popper">
          🎉
        </span>
        <h2 className="text-lg font-semibold text-green-800 dark:text-green-200">
          Tailwind + Dark Mode Active
        </h2>
      </div>
      <button
        onClick={onClose}
        className="text-sm font-medium text-green-700 dark:text-green-300 hover:underline focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
        aria-label="Dismiss banner"
      >
        Dismiss
      </button>
    </div>
  );
}

function LoadingFallback(): JSX.Element {
  return (
    <div className="flex items-center justify-center p-12">
      <div className="text-center space-y-3">
        <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading...</p>
      </div>
    </div>
  );
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  return user ? <Home /> : <Landing />;
}

export default function App(): JSX.Element {
  const [showBanner, setShowBanner] = React.useState(false);

  return (
    <div
      className={[
        "min-h-screen",
        "text-gray-900 dark:text-gray-100",
        "bg-gradient-to-b from-blue-50 to-gray-100",
        "dark:bg-gradient-to-b dark:from-slate-900 dark:to-slate-950",
        "transition-colors duration-300",
      ].join(" ")}
    >
      <header className="max-w-6xl mx-auto px-4 py-3 sticky top-0 z-40 backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 border-b border-black/5 dark:border-white/5">
        <Navbar />
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {showBanner && (
          <DismissibleBanner
            show={showBanner}
            onClose={() => setShowBanner(false)}
          />
        )}

        <React.Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/landing" element={<Landing />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/video" element={<VideoPlayer />} />
            <Route path="/share/:slug" element={<SharePage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/backgrounds" element={<Backgrounds />} />
            <Route path="/animated-bg" element={<AnimatedBg />} />
            <Route path="/animated-quiz" element={<RequireAuth><AnimatedQuiz /></RequireAuth>} />
            <Route path="/pricing" element={<Navigate to="/credits" replace />} />
            <Route path="/credits" element={<RequireAuth><CreditsPage /></RequireAuth>} />
            <Route path="/flashcards" element={<RequireAuth><FlashcardsPage /></RequireAuth>} />
            <Route path="/flashcards/topic/:slug" element={<PSEOFlashcardsLanding />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/account" element={<AccountPage />} />

            {/* Redirect legacy/duplicate paths */}
            <Route
              path="/historyPage"
              element={<Navigate to="/history" replace />}
            />
            <Route
              path="/animated"
              element={<Navigate to="/animated-bg" replace />}
            />
            <Route
              path="/renders/:id"
              element={<RenderRedirect />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/layout"
              element={
                <AdminRoute>
                  <LayoutEditor />
                </AdminRoute>
              }
            />

            {/* Render Editor Routes */}
            <Route path="/renderEditor" element={<RenderEditor />} />
            <Route path="/renders/:id/edit" element={<RenderEditor />} />

            {/* 404 Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </React.Suspense>
      </main>

      {/* Footer with disclaimer */}
      <Footer />
    </div>
  );
}